import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import groups from "../components/groups/Groups.module.css";
import { Icon } from "../components/Icon.tsx";
import statePanel from "../components/StatePanel.module.css";
import { StatePanel } from "../components/StatePanel.tsx";
import { NewTokenButton, NewTokenDialog } from "../components/tokens/NewTokenButton.tsx";
import styles from "../components/tokens/Tokens.module.css";
import { TokenTable } from "../components/tokens/TokenTable.tsx";
import ui from "../components/ui.module.css";
import { formatNumber } from "../lib/format.ts";
import { messageOf, tokensQuery } from "../lib/queries.ts";

export const Route = createFileRoute("/tokens")({
  loader: ({ context }) => context.queryClient.ensureQueryData(tokensQuery),
  component: TokensPage,
  pendingComponent: TokensPending,
  errorComponent: TokensError,
});

function TokensPage() {
  const { data } = useSuspenseQuery(tokensQuery);
  const tokens = data.items;
  const active = tokens.filter((token) => token.state === "active").length;
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    document.title = "API tokens · Vault";
  }, []);

  return (
    <div className={groups.page}>
      <div className={groups.intro}>
        <div className={groups.heading}>
          <h1 className={groups.title}>API tokens</h1>
          {tokens.length > 0 ? (
            <dl className={groups.stats}>
              <div>
                <dt>Active</dt>
                <dd data-testid="active-count">{formatNumber(active)}</dd>
              </div>
            </dl>
          ) : null}
        </div>
        {tokens.length > 0 ? (
          <NewTokenButton className={groups.pageAction} onClick={() => setCreating(true)} />
        ) : null}
      </div>

      {tokens.length === 0 ? (
        <div className={`${ui.card} ${groups.empty}`}>
          <span className={statePanel.icon} aria-hidden="true">
            <Icon name="key" />
          </span>
          <h2 className={groups.emptyTitle}>No API tokens</h2>
          <p className={groups.emptyDetail}>
            A token lets a script use the drive as you, within the scopes and folder you give it.
          </p>
          <NewTokenButton onClick={() => setCreating(true)} />
        </div>
      ) : (
        <TokenTable tokens={tokens} />
      )}

      {creating ? <NewTokenDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function TokensPending() {
  return (
    <div className={groups.page} aria-busy="true">
      <div className={groups.intro}>
        <div className={groups.heading}>
          <h1 className={groups.title}>API tokens</h1>
          <span className={`${ui.skeleton} ${groups.statsSkeleton}`} />
        </div>
      </div>
      <div className={`${ui.card} ${styles.pendingList}`}>
        {["first", "second", "third"].map((key) => (
          <div key={key} className={groups.pendingRow}>
            <span className={ui.skeleton} />
            <span className={ui.skeleton} />
          </div>
        ))}
      </div>
    </div>
  );
}

function TokensError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  return (
    <StatePanel icon="alert" title="API tokens did not load" detail={messageOf(error)}>
      <button
        type="button"
        className={ui.button}
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
